import React, {useState} from 'react';
import {useSwipeable} from 'react-swipeable';
import blueTab from '../../images/homepage/blue-tab.png';
import {Feature} from '../../types';
import {GatsbyImage} from 'gatsby-plugin-image';
import {thinMargins} from '../../constants';
import {useSetCarouselInterval} from '../../utils';
import {StyledStructuredText} from '../../components/common/StyledStructuredText';
import {StructuredTextDocument} from 'react-datocms/structured-text';

interface IFeatureDescription {
  title: string;
  description: string;
  activeTab: Feature;
  onClick: () => void;
}

const FeatureDescription = ({
  title,
  description,
  activeTab,
  onClick,
}: IFeatureDescription) => {
  const isActive = activeTab.title === title;
  const activeStyle = 'opacity-100';
  const inactiveStyle = 'opacity-50';
  return (
    <div className="flex pt-3">
      <div className="flex gap-7 items-center">
        <img
          src={blueTab}
          style={{
            visibility: isActive ? 'visible' : 'hidden',
            height: 'max-content',
          }}
        />
        <div
          className={`cursor-pointer ${isActive ? activeStyle : inactiveStyle}`}
          onClick={onClick}>
          <h3 className="text-black text-subtitlelarge font-bold">{title}</h3>
          <p className="text-black text-largebody">{description}</p>
        </div>
      </div>
    </div>
  );
};

interface IMobileFeature {
  feature: Feature;
  context: 'parent' | 'teen' | 'home' | 'about';
  centerText?: boolean;
}

const isHomeContext = (context: IMobileFeature['context']) =>
  context === 'home';

const MobileFeatureImage = ({feature, context}: IMobileFeature) => {
  const isHome = isHomeContext(context);
  return (
    <div className="text-black mx-[5%] min-w-[90%] flex-shrink-0">
      <div
        className={` ${
          isHome ? 'h-[27rem] bg-[#FFECB0]' : 'max-h-[20rem]'
        } rounded-3xl overflow-hidden`}>
        <GatsbyImage
          alt={feature.image.alt}
          image={feature.image.gatsbyImageData}
          className={`mx-auto ${
            isHome
              ? 'mt-7 max-w-[18rem]'
              : 'max-w-[20rem] md:max-w-[22rem] rounded-[2rem] object-contain'
          }`}
          draggable={false}
        />
      </div>
    </div>
  );
};

const MobileFeatureText = ({feature, centerText = false}: IMobileFeature) => {
  return (
    <div
      className={`text-black px-2 mx-[5%] ${centerText ? 'pt-1 text-center' : 'pt-1 text-start'}`}>
      <h3 className="text-subtitlelargemob font-bold">{feature.title}</h3>
      <p className="text-largebodymob pt-2">{feature.description}</p>
      {feature.explainer && (
        <div
          className={`text-bodymob pt-1 ${centerText ? 'text-center' : 'text-start'}`}>
          <StyledStructuredText
            data={feature.explainer.value as unknown as StructuredTextDocument}
          />
        </div>
      )}
    </div>
  );
};

export const MobileFeatureSelector = ({
  features,
  context,
  centerText = false,
}: {
  features: Feature[];
  context: 'parent' | 'teen' | 'home' | 'about';
  centerText?: boolean;
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);

  const previousSlide = () => {
    setIsSlideshowPaused(true);
    if (currentSlideIndex === 0) setCurrentSlideIndex(features?.length - 1);
    else setCurrentSlideIndex(currentSlideIndex - 1);
  };

  const nextSlide = () => {
    setIsSlideshowPaused(true);
    if (currentSlideIndex === features.length - 1) setCurrentSlideIndex(0);
    else setCurrentSlideIndex(currentSlideIndex + 1);
  };

  const isLastSlide = () => {
    if (currentSlideIndex === features.length) return true;
    else return false;
  };

  const [isSlideshowPaused, setIsSlideshowPaused] = useState(false);

  const handlers = useSwipeable({
    onSwipedLeft: () => nextSlide(),
    onSwipedRight: () => previousSlide(),
    trackMouse: true,
    preventScrollOnSwipe: true,
    onTap: () => {
      setIsSlideshowPaused(true);
    },
  });

  useSetCarouselInterval({
    isSlideshowPaused,
    currentSlideIndex,
    lastSlideIndex: features.length,
    onSetCurrentSlideIndex: setCurrentSlideIndex,
    time: 3000,
  });

  const currentFeature = features[currentSlideIndex];

  return (
    <div className="lg:hidden" {...handlers}>
      <div className="overflow-hidden">
        <div
          className="flex transition ease-out duration-200"
          style={{
            transform: `${
              isLastSlide() ? '' : `translatex(-${currentSlideIndex * 100}%)`
            }`,
          }}>
          {features.map((feature, i) => (
            <MobileFeatureImage key={i} feature={feature} context={context} />
          ))}
        </div>
      </div>

      <div className="pt-5 pb-4 flex justify-center gap-2 w-full">
        {features.map((f, i) => (
          <button
            type="button"
            onClick={() => setCurrentSlideIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`w-10 h-1 rounded-sm ${context === 'about' ? 'bg-sandwisp' : 'bg-white'} ${i === currentSlideIndex ? 'bg-opacity-100' : 'bg-opacity-40'}`}
            key={i}
          />
        ))}
      </div>
      {currentFeature && (
        <MobileFeatureText
          feature={currentFeature}
          context={context}
          centerText={centerText}
        />
      )}
    </div>
  );
};

export const FeatureSelector = ({features}: {features: Feature[]}) => {
  const [activeTab, setActiveTab] = useState<Feature>(features[0]);

  return (
    <>
      <div
        className={`hidden lg:flex justify-center gap-32 pb-20 xl:mx-40 2xl:mx-72 ${thinMargins}`}>
        <div className="w-1/2">
          {features.map((feature, i) => (
            <FeatureDescription
              key={i}
              title={feature.title}
              description={feature.description}
              onClick={() => {
                setActiveTab(feature);
              }}
              activeTab={activeTab}
            />
          ))}
        </div>
        <div className="w-1/3">
          <GatsbyImage
            alt={activeTab?.image.alt || ''}
            image={activeTab.image.gatsbyImageData}
            className="max-w-[25rem] max-h-[51rem]"
            draggable={false}
          />
        </div>
      </div>
      <MobileFeatureSelector features={features} context="home" />
    </>
  );
};
